/* ==========================================================================
   Myla's Moss — theme.js
   Vanilla JS, no build step. Every module checks for its own markup before
   running, so partial pages (only some sections present) never error.
   All motion respects `body[data-motion="off"]` (theme setting) AND
   prefers-reduced-motion, whichever is stricter.
   ========================================================================== */
(function () {
  'use strict';

  var MM = (window.MM = window.MM || {});
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionEnabled =
    document.body.getAttribute('data-motion') !== 'off' && !prefersReducedMotion;
  MM.motionEnabled = motionEnabled;

  /* ------------------------------------------------------------------
     Scroll reveal — fade + 14px rise, staggered
     Opt in by adding class="reveal" to any element.
     ------------------------------------------------------------------ */
  function initScrollReveal() {
    var els = document.querySelectorAll('.reveal:not(.reveal-init)');
    if (!els.length) return;
    els.forEach(function (el, i) {
      el.classList.add('reveal-init');
      if (!motionEnabled) {
        el.classList.add('reveal-in');
        return;
      }
      el.classList.add('reveal-pre');
      el.style.transitionDelay = (i % 4) * 70 + 'ms';
    });
    if (!motionEnabled) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('reveal-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
    setTimeout(function () {
      document.querySelectorAll('.reveal-pre:not(.reveal-in)').forEach(function (el) {
        el.classList.add('reveal-in');
      });
    }, 1800);
  }

  /* ------------------------------------------------------------------
     Countdown — used by the 3-Month Challenge urgency banner.
     Markup: <div data-countdown data-end="2026-08-31T23:59:59+01:00"
                   data-ended-text="This offer has now ended.">
               <span data-cd-d></span> <span data-cd-h></span> <span data-cd-m></span>
             </div>
     ------------------------------------------------------------------ */
  function initCountdowns() {
    var roots = document.querySelectorAll('[data-countdown]:not([data-cd-init])');
    roots.forEach(function (root) {
      root.setAttribute('data-cd-init', '1');
      var end = new Date(root.getAttribute('data-end')).getTime();
      var endedText = root.getAttribute('data-ended-text') || 'This offer has now ended.';
      var dEl = root.querySelector('[data-cd-d]');
      var hEl = root.querySelector('[data-cd-h]');
      var mEl = root.querySelector('[data-cd-m]');
      var liveEl = root.querySelector('[data-cd-live]');
      var wrap = root.querySelector('[data-cd-wrap]') || root;
      if (isNaN(end)) return;
      function tick() {
        var ms = end - Date.now();
        if (ms <= 0) {
          wrap.textContent = endedText;
          root.setAttribute('data-cd-ended', '1');
          root.dispatchEvent(new CustomEvent('countdown:ended'));
          return;
        }
        var d = Math.floor(ms / 864e5);
        var h = Math.floor((ms % 864e5) / 36e5);
        var m = Math.floor((ms % 36e5) / 6e4);
        if (dEl) dEl.textContent = d;
        if (hEl) hEl.textContent = h;
        if (mEl) mEl.textContent = m;
        if (liveEl) liveEl.setAttribute('datetime', new Date(Date.now() + ms).toISOString());
        setTimeout(tick, 30000);
      }
      tick();
    });
  }

  /* ------------------------------------------------------------------
     Buy box — subscription / one-time toggle + add-ons + qty + AJAX add.
     Markup contract (see snippets/buy-box.liquid):
       .buybox[data-product-handle]
         .opt[data-variant-id][data-selling-plan-id?][data-price]
         .addons .addon input[type=checkbox][data-variant-id][data-price]
         .qty button[data-qty-down] / span[data-qty-value] / button[data-qty-up]
         a.btn.primary[data-atc-btn][data-label-template="Add to basket — {price}"]
     ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    return (format || '£{{amount}}').replace('{{amount}}', value);
  }

  function initBuyBoxes() {
    var boxes = document.querySelectorAll('.buybox:not([data-buybox-init])');
    boxes.forEach(function (box) {
      box.setAttribute('data-buybox-init', '1');
      var btn = box.querySelector('[data-atc-btn]');
      var qtyValue = box.querySelector('[data-qty-value]');
      var qtyDown = box.querySelector('[data-qty-down]');
      var qtyUp = box.querySelector('[data-qty-up]');
      var moneyFormat = box.getAttribute('data-money-format') || '£{{amount}}';
      var labelTemplate = btn
        ? btn.getAttribute('data-label-template') || 'Add to basket — {price}'
        : '';

      var addonsEl = box.querySelector('.addons');

      function selectedOption() {
        return box.querySelector('.opt.sel') || box.querySelector('.opt');
      }

      function updateAddonsVisibility() {
        if (!addonsEl) return;
        var opt = selectedOption();
        var isSubscription = !!(opt && opt.getAttribute('data-selling-plan-id'));
        addonsEl.hidden = isSubscription;
        if (isSubscription) {
          box.querySelectorAll('.addon input:checked').forEach(function (c) {
            c.checked = false;
          });
        }
      }
      updateAddonsVisibility();

      function qty() {
        return parseInt((qtyValue && qtyValue.textContent) || '1', 10) || 1;
      }

      function mixMatch(opt, baseCents) {
        /* Mix & match (Settings > Discounts): any 2 eligible ONE-TIME items save 10%, 3+ save 20%.
           Subscriptions never count. The saving is applied automatically at checkout, so this
           mirrors it here: the button total, the ticked add-on rows and a "you save" line all
           show what will actually be charged. Percentages come from data-mm-two / data-mm-three
           on .buybox (default 10 / 20); keep them equal to the two automatic discounts. */
        var q = qty();
        var isSub = !!(opt && opt.getAttribute('data-selling-plan-id'));
        var two = parseInt(box.getAttribute('data-mm-two'), 10) || 10;
        var three = parseInt(box.getAttribute('data-mm-three'), 10) || 20;
        var lines = [{ cents: baseCents, on: true, ok: !isSub && box.getAttribute('data-mm-main') !== '0', input: null }];
        box.querySelectorAll('.addon input').forEach(function (c) {
          lines.push({
            cents: parseInt(c.getAttribute('data-price-cents'), 10) || 0,
            on: c.checked,
            ok: c.getAttribute('data-mm') !== '0',
            input: c
          });
        });
        var n = 0;
        lines.forEach(function (l) { if (l.on && l.ok) n += q; });
        var pct = n >= 3 ? three : n >= 2 ? two : 0;
        var total = 0;
        var saved = 0;
        lines.forEach(function (l) {
          if (!l.on) return;
          var line = l.cents * q;
          var off = l.ok && pct ? Math.round((line * pct) / 100) : 0;
          total += line - off;
          saved += off;
        });
        lines.forEach(function (l) {
          if (!l.input) return;
          var row = l.input.closest('.addon');
          var ap = row && row.querySelector('.ap');
          if (!ap) return;
          if (!ap.hasAttribute('data-orig')) ap.setAttribute('data-orig', ap.innerHTML);
          if (l.on && l.ok && pct) {
            ap.innerHTML =
              '+' + formatMoney(l.cents - Math.round((l.cents * pct) / 100), moneyFormat) +
              ' <s>' + formatMoney(l.cents, moneyFormat) + '</s>';
          } else {
            ap.innerHTML = ap.getAttribute('data-orig');
          }
        });
        var note = box.querySelector('[data-mm-save]');
        if (!note) {
          var cta = box.querySelector('.cta-row');
          if (cta) {
            note = document.createElement('div');
            note.className = 'mm-save';
            note.setAttribute('data-mm-save', '');
            note.hidden = true;
            cta.insertAdjacentElement('beforebegin', note);
          }
        }
        if (note) {
          if (saved > 0) {
            note.textContent =
              'Mix & match: ' + pct + '% off applied, you save ' + formatMoney(saved, moneyFormat) +
              (n === 2 ? '. Add one more item for ' + three + '% off.' : '.');
            note.hidden = false;
          } else {
            note.hidden = true;
          }
        }
        return total;
      }
      function updateTotal() {
        var opt = selectedOption();
        var baseCents = opt ? parseInt(opt.getAttribute('data-price-cents'), 10) || 0 : 0;
        var totalCents = mixMatch(opt, baseCents);
        box.querySelectorAll('.addon input:checked').forEach(function (c) {
          void 0;
        });
        if (btn) {
          var priceStr = formatMoney(totalCents, moneyFormat);
          btn.textContent = labelTemplate.replace('{price}', priceStr);
        }
        box.dispatchEvent(new CustomEvent('buybox:total', { detail: { cents: totalCents } }));
      }

      box.querySelectorAll('.opt').forEach(function (opt) {
        opt.addEventListener('click', function () {
          box.querySelectorAll('.opt').forEach(function (o) {
            o.classList.remove('sel');
          });
          opt.classList.add('sel');
          updateAddonsVisibility();
          updateTotal();
        });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            opt.click();
          }
        });
      });

      box.querySelectorAll('.addon input').forEach(function (input) {
        input.addEventListener('change', updateTotal);
      });

      if (qtyUp) {
        qtyUp.addEventListener('click', function () {
          if (qtyValue) qtyValue.textContent = qty() + 1;
          updateTotal();
        });
      }
      if (qtyDown) {
        qtyDown.addEventListener('click', function () {
          if (qtyValue) qtyValue.textContent = Math.max(1, qty() - 1);
          updateTotal();
        });
      }

      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          addBuyBoxToCart(box, qty());
        });
      }

      updateTotal();
    });
  }

  function addBuyBoxToCart(box, quantity) {
    var opt = box.querySelector('.opt.sel') || box.querySelector('.opt');
    if (!opt) return;
    var items = [];
    var variantId = opt.getAttribute('data-variant-id');
    var sellingPlanId = opt.getAttribute('data-selling-plan-id');
    if (variantId) {
      var mainItem = { id: parseInt(variantId, 10), quantity: quantity };
      if (sellingPlanId) mainItem.selling_plan = parseInt(sellingPlanId, 10);
      items.push(mainItem);
    }
    box.querySelectorAll('.addon input:checked').forEach(function (c) {
      var addonVariantId = c.getAttribute('data-variant-id');
      if (addonVariantId) {
        items.push({ id: parseInt(addonVariantId, 10), quantity: quantity });
      }
    });
    if (!items.length) return;

    var btn = box.querySelector('[data-atc-btn]');
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
    }

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(function (err) {
        console.error('Add to cart failed', err);
        document.dispatchEvent(
          new CustomEvent('cart:error', { detail: { message: (err && err.description) || 'Could not add to basket.' } })
        );
      })
      .finally(function () {
        if (btn) {
          btn.removeAttribute('aria-busy');
          btn.classList.remove('is-loading');
        }
      });
  }
  MM.addBuyBoxToCart = addBuyBoxToCart;

  /* ------------------------------------------------------------------
     Simple "Add" buttons on product/collection cards (single variant,
     no options UI — used by bestseller/cross-sell/collection cards).
     Markup: button/a[data-simple-atc][data-variant-id][data-selling-plan-id?]
     ------------------------------------------------------------------ */
  function initSimpleAtc() {
    document.querySelectorAll('[data-simple-atc]:not([data-simple-atc-init])').forEach(function (el) {
      el.setAttribute('data-simple-atc-init', '1');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = el.getAttribute('data-variant-id');
        if (!variantId) return;
        var item = { id: parseInt(variantId, 10), quantity: 1 };
        var sellingPlanId = el.getAttribute('data-selling-plan-id');
        if (sellingPlanId) item.selling_plan = parseInt(sellingPlanId, 10);
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        })
          .then(function (res) { return res.json(); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            document.dispatchEvent(new CustomEvent('cart:open'));
          })
          .catch(function (err) { console.error('Add to cart failed', err); });
      });
    });
  }

  /* ------------------------------------------------------------------
     Card size picker — lets shoppers switch which variant's size pill
     is selected on a .scard tile before adding to basket (previously
     the pills were decorative only and always added the first size).
     Markup: .scard[data-money-format] .variants .v[data-variant-id][data-price-cents][data-compare-cents]
  ------------------------------------------------------------------ */
  function initCardVariants() {
    document.querySelectorAll('.scard .variants:not([data-card-variants-init])').forEach(function (wrap) {
      wrap.setAttribute('data-card-variants-init', '1');
      var card = wrap.closest('.scard');
      if (!card) return;
      var moneyFormat = card.getAttribute('data-money-format') || '£{{amount}}';
      var priceEl = card.querySelector('[data-card-price]');
      var compareEl = card.querySelector('[data-card-compare]');
      var subEl = card.querySelector('.sub');
      var addBtn = card.querySelector('[data-simple-atc]');

      wrap.querySelectorAll('.v[data-variant-id]').forEach(function (pill) {
        pill.addEventListener('click', function () {
          wrap.querySelectorAll('.v').forEach(function (p) { p.classList.remove('on'); });
          pill.classList.add('on');

          var priceCents = parseInt(pill.getAttribute('data-price-cents'), 10) || 0;
          var compareCents = parseInt(pill.getAttribute('data-compare-cents'), 10) || 0;

          if (priceEl) priceEl.textContent = formatMoney(priceCents, moneyFormat);
          if (compareEl) {
            if (compareCents > priceCents) {
              compareEl.textContent = formatMoney(compareCents, moneyFormat);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
          if (subEl) subEl.hidden = true;

          if (addBtn) {
            addBtn.setAttribute('data-variant-id', pill.getAttribute('data-variant-id'));
            addBtn.removeAttribute('data-selling-plan-id');
          }
        });
        pill.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pill.click();
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Card size picker — lets shoppers switch which variant's size pill
     is selected on a .scard tile before adding to basket (previously
     the pills were decorative only and always added the first size).
     Markup: .scard[data-money-format] .variants .v[data-variant-id][data-price-cents][data-compare-cents]
  ------------------------------------------------------------------ */
  function initCardVariants() {
    document.querySelectorAll('.scard .variants:not([data-card-variants-init])').forEach(function (wrap) {
      wrap.setAttribute('data-card-variants-init', '1');
      var card = wrap.closest('.scard');
      if (!card) return;
      var moneyFormat = card.getAttribute('data-money-format') || '£{{amount}}';
      var priceEl = card.querySelector('[data-card-price]');
      var compareEl = card.querySelector('[data-card-compare]');
      var subEl = card.querySelector('.sub');
      var addBtn = card.querySelector('[data-simple-atc]');

      wrap.querySelectorAll('.v[data-variant-id]').forEach(function (pill) {
        pill.addEventListener('click', function () {
          wrap.querySelectorAll('.v').forEach(function (p) { p.classList.remove('on'); });
          pill.classList.add('on');

          var priceCents = parseInt(pill.getAttribute('data-price-cents'), 10) || 0;
          var compareCents = parseInt(pill.getAttribute('data-compare-cents'), 10) || 0;

          if (priceEl) priceEl.textContent = formatMoney(priceCents, moneyFormat);
          if (compareEl) {
            if (compareCents > priceCents) {
              compareEl.textContent = formatMoney(compareCents, moneyFormat);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
          if (subEl) subEl.hidden = true;

          if (addBtn) {
            addBtn.setAttribute('data-variant-id', pill.getAttribute('data-variant-id'));
            addBtn.removeAttribute('data-selling-plan-id');
          }
        });
        pill.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pill.click();
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Moss Lab quiz — config supplied via a JSON <script type="application/json">
     inside the section, so recommendations map to real products/variants
     without touching this file. See sections/moss-lab-quiz.liquid.
     ------------------------------------------------------------------ */
  function initMossLabQuiz() {
    var roots = document.querySelectorAll('[data-mosslab]:not([data-mosslab-init])');
    roots.forEach(function (root) {
      root.setAttribute('data-mosslab-init', '1');
      var configEl = root.querySelector('[data-mosslab-config]');
      var config = {};
      try {
        config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (e) {
        console.error('Moss Lab quiz: invalid config JSON', e);
        return;
      }
      var answers = {};
      var rules = config.rules || [];
      var fallback = config.fallback || null;

      function matchRule(rule, a) {
        return Object.keys(rule.when || {}).every(function (key) {
          var want = rule.when[key];
          if (Array.isArray(want)) return want.indexOf(a[key]) !== -1;
          return a[key] === want;
        });
      }

      function recommend(a) {
        for (var i = 0; i < rules.length; i++) {
          if (matchRule(rules[i], a)) return rules[i].result;
        }
        return fallback;
      }

      root.addEventListener('click', function (e) {
        var opt = e.target.closest('.q-opt');
        if (opt) {
          answers[opt.dataset.k] = opt.dataset.v;
          var step = opt.closest('.q-step');
          var nextStep = root.querySelector(
            '.q-step[data-step="' + (parseInt(step.dataset.step, 10) + 1) + '"]'
          );
          step.hidden = true;
          if (nextStep) {
            nextStep.hidden = false;
          } else {
            var result = recommend(answers);
            if (!result) return;
            var titleEl = root.querySelector('[data-q-title]');
            var whyEl = root.querySelector('[data-q-why]');
            var priceEl = root.querySelector('[data-q-price]');
            var ctaEl = root.querySelector('[data-q-cta]');
            if (titleEl) titleEl.textContent = result.title || '';
            if (whyEl) whyEl.textContent = result.why || '';
            if (priceEl) priceEl.textContent = result.price_label || '';
            if (ctaEl && result.url) ctaEl.setAttribute('href', result.url);
            if (ctaEl && result.variant_id) {
              ctaEl.setAttribute('data-simple-atc', '1');
              ctaEl.setAttribute('data-variant-id', result.variant_id);
              if (result.selling_plan_id) ctaEl.setAttribute('data-selling-plan-id', result.selling_plan_id);
              initSimpleAtc();
            }
            var pairEl = root.querySelector('[data-q-pair]');
            var herb = (config.herb_pairings || {})[answers.goal];
            if (pairEl && herb) {
              var herbNameEl = root.querySelector('[data-q-herb]');
              var herbPriceEl = root.querySelector('[data-q-herb-price]');
              var herbAddEl = root.querySelector('[data-q-herb-add]');
              if (herbNameEl) herbNameEl.textContent = herb.title;
              if (herbPriceEl) herbPriceEl.textContent = herb.price_label;
              if (herbAddEl && herb.variant_id) {
                herbAddEl.setAttribute('data-simple-atc', '1');
                herbAddEl.setAttribute('data-variant-id', herb.variant_id);
                initSimpleAtc();
              }
              pairEl.hidden = false;
            }
            root.querySelector('.q-result').hidden = false;
          }
        }
        if (e.target.classList.contains('q-restart')) {
          answers = {};
          var resultEl = root.querySelector('.q-result');
          if (resultEl) resultEl.hidden = true;
          var pairEl2 = root.querySelector('[data-q-pair]');
          if (pairEl2) pairEl2.hidden = true;
          root.querySelectorAll('.q-step').forEach(function (s) {
            s.hidden = s.dataset.step !== '1';
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Cart drawer — open/close + free-shipping meter + line item render.
     Relies on Shopify's ajax cart.js endpoint. Section: sections/cart-drawer.liquid
     ------------------------------------------------------------------ */
  var CartDrawer = {
    el: null,
    init: function () {
      this.el = document.querySelector('[data-cart-drawer]');
      if (!this.el) return;
      var self = this;
      document.addEventListener('cart:open', function () { self.open(); });
      document.addEventListener('cart:updated', function () { self.refresh(); });
      this.el.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-drawer-close]') || e.target === self.el.querySelector('[data-cart-drawer-overlay]')) {
          self.close();
        }
      });
      document.querySelectorAll('[data-cart-drawer-open]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          self.open();
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
      });
      this.refresh();
    },
    open: function () {
      if (!this.el) return;
      this.el.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
    },
    close: function () {
      if (!this.el) return;
      this.el.removeAttribute('data-open');
      document.body.style.overflow = '';
    },
    refresh: function () {
      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) { self.render(cart); })
        .catch(function (err) { console.error('Cart refresh failed', err); });
    },
    render: function (cart) {
      if (!this.el) return;
      var countEls = document.querySelectorAll('[data-cart-count]');
      countEls.forEach(function (el) { el.textContent = cart.item_count; });

      var threshold = parseInt(this.el.getAttribute('data-free-shipping-threshold-cents') || '0', 10);
      var meterBar = this.el.querySelector('[data-shipping-meter-bar]');
      var meterText = this.el.querySelector('[data-shipping-meter-text]');
      if (threshold > 0 && meterBar && meterText) {
        var remaining = threshold - cart.total_price;
        var pct = Math.min(100, Math.max(0, (cart.total_price / threshold) * 100));
        meterBar.style.width = pct + '%';
        if (remaining <= 0) {
          meterText.textContent = meterText.getAttribute('data-earned-text') || "You've unlocked free delivery";
        } else {
          var tmpl = meterText.getAttribute('data-progress-text') || '%amount% away from free delivery';
          meterText.textContent = tmpl.replace('%amount%', formatMoney(remaining, this.el.getAttribute('data-money-format') || '£{{amount}}'));
        }
      }

      var itemsWrap = this.el.querySelector('[data-cart-items]');
      var emptyState = this.el.querySelector('[data-cart-empty]');
      if (itemsWrap) {
        itemsWrap.innerHTML = '';
        if (!cart.items.length && emptyState) {
          emptyState.hidden = false;
        } else {
          if (emptyState) emptyState.hidden = true;
          var self = this;
          cart.items.forEach(function (item) {
            var row = document.createElement('div');
            row.className = 'cart-drawer__item';
            row.innerHTML =
              '<a href="' + item.url + '"><img src="' + item.image + '" alt="" width="72" height="72" loading="lazy"></a>' +
              '<div class="cart-drawer__item-body">' +
              '<p class="cart-drawer__item-title"><a href="' + item.url + '" style="color:inherit;text-decoration:none">' + item.product_title + '</a></p>' +
              '<p class="cart-drawer__item-badge" data-badge>' +
              (item.selling_plan_allocation ? 'Subscription' : 'Made fresh to order') +
              '</p>' +
              '<p class="cart-drawer__item-meta">' + (item.variant_title ? item.variant_title + ' &middot; ' : '') + 'Qty ' + item.quantity + '</p>' +
              '<button type="button" class="cart-drawer__sub-toggle" data-sub-toggle hidden></button>' +
              '<button type="button" class="cart-drawer__remove" data-remove-line aria-label="Remove">Remove</button>' +
              '</div>' +
              '<p class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>';

            row.querySelector('[data-remove-line]').addEventListener('click', function () {
              self.changeLine(item.key, 0);
            });

            itemsWrap.appendChild(row);
            self.enhanceSubscriptionToggle(row, item);
          });
        }
      }
    },
    /* Adds a "Subscribe & save" / "Switch to one-time" link per line by
       looking up the product's real selling plan groups. Cached per handle
       so repeat renders don't refetch. */
    productCache: {},
    enhanceSubscriptionToggle: function (row, item) {
      var self = this;
      var toggleBtn = row.querySelector('[data-sub-toggle]');
      if (!toggleBtn || !item.handle) return;
      var lookup = this.productCache[item.handle]
        ? Promise.resolve(this.productCache[item.handle])
        : fetch('/products/' + item.handle + '.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (product) {
              self.productCache[item.handle] = product;
              return product;
            })
            .catch(function () { return null; });

      lookup.then(function (product) {
        if (!product || !product.selling_plan_groups || !product.selling_plan_groups.length) return;
        var firstPlan = product.selling_plan_groups[0].selling_plans[0];
        if (!firstPlan) return;

        if (item.selling_plan_allocation) {
          toggleBtn.textContent = 'Switch to one-time purchase';
          toggleBtn.hidden = false;
          toggleBtn.addEventListener('click', function () {
            self.swapLine(item, { id: item.variant_id, quantity: item.quantity });
          });
        } else {
          toggleBtn.textContent = 'Subscribe & save';
          toggleBtn.hidden = false;
          toggleBtn.addEventListener('click', function () {
            self.swapLine(item, {
              id: item.variant_id,
              quantity: item.quantity,
              selling_plan: firstPlan.id
            });
          });
        }
      });
    },
    changeLine: function (key, quantity) {
      var self = this;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function () { document.dispatchEvent(new CustomEvent('cart:updated')); })
        .catch(function (err) { console.error('Cart change failed', err); });
    },
    swapLine: function (item, newItem) {
      var self = this;
      // Remove the existing line, then add the replacement — Shopify's
      // cart API has no direct "change selling plan on an existing line"
      // call, so a clean remove + re-add is the reliable path.
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [newItem] })
          });
        })
        .then(function () { document.dispatchEvent(new CustomEvent('cart:updated')); })
        .catch(function (err) { console.error('Cart swap failed', err); });
    }
  };
  MM.CartDrawer = CartDrawer;

  /* ------------------------------------------------------------------
     Sticky mobile add-to-cart bar (recommended addition, PDP only).
     Shows once the primary buy box has scrolled out of view.
     ------------------------------------------------------------------ */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    var buybox = document.querySelector('.buybox [data-atc-btn]') || document.querySelector('.buybox');
    if (!bar || !buybox || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
bar.classList.toggle('is-visible', !entry.isIntersecting && window.innerWidth < 780);
        });
      },
      { threshold: 0 }
    );
   io.observe(buybox); var syncBox = document.querySelector('.buybox'); if (syncBox) syncBox.addEventListener('buybox:total', function (e) { var p = formatMoney(e.detail.cents, syncBox.getAttribute('data-money-format') || '£{{amount}}'); var pe = bar.querySelector('.sticky-atc__price'); if (pe) pe.textContent = p; var pb = bar.querySelector('[data-atc-btn]'); if (pb) pb.textContent = 'Add · ' + p; });
    var btn = bar.querySelector('[data-atc-btn]');
    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var box = document.querySelector('.buybox');
if (box) { var qv = box.querySelector('[data-qty-value]'); addBuyBoxToCart(box, (qv && parseInt(qv.textContent, 10)) || 1); }
      });
    }
  }

  /* ------------------------------------------------------------------
     Newsletter / exit-intent + 20s delayed email capture
     (replaces the retired spin-to-win popup per brief).
     ------------------------------------------------------------------ */
  function initEmailCapture() {
    var popup = document.querySelector('[data-email-capture]');
    if (!popup) return;
    var dismissedKey = 'mm_email_capture_dismissed';
    var shown = false;
    function maybeShow() {
      if (shown) return;
      try {
        if (sessionStorage.getItem(dismissedKey)) return;
      } catch (e) { /* storage unavailable, still allow showing */ }
      shown = true;
      popup.setAttribute('data-open', 'true');
    }
    function dismiss() {
      popup.removeAttribute('data-open');
      try { sessionStorage.setItem(dismissedKey, '1'); } catch (e) {}
    }
    popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-email-capture-close]') || e.target === popup) dismiss();
    });
    setTimeout(maybeShow, 20000);
    document.addEventListener('mouseout', function (e) {
      if (e.clientY < 0) maybeShow();
    });
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */
  function initSubscribeInfoTooltip() {
    if (window.__infoTooltipInit) return;
    window.__infoTooltipInit = true;

    function closeAll(except) {
      document.querySelectorAll('.info-i.open').forEach(function (el) {
        if (el !== except) el.classList.remove('open');
      });
    }

    document.addEventListener('click', function (e) {
      var icon = e.target.closest('.info-i');
      if (icon) {
        e.stopPropagation();
        icon.classList.toggle('open');
        closeAll(icon);
      } else {
        closeAll();
      }
    }, true);

    document.addEventListener('keydown', function (e) {
      var icon = e.target.closest && e.target.closest('.info-i');
      if (icon && (e.key === 'Enter' || e.key === ' ')) {
        e.stopPropagation();
        e.preventDefault();
        icon.classList.toggle('open');
        closeAll(icon);
      } else if (e.key === 'Escape') {
        closeAll();
      }
    }, true);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initScrollReveal();
    initCountdowns();
    initBuyBoxes();
    initSubscribeInfoTooltip();
    initSimpleAtc();
    initCardVariants();
    initMossLabQuiz();
    CartDrawer.init();
    initStickyAtc();
    initEmailCapture();
  });

  // Re-run idempotent initializers after Shopify section re-renders
  // (theme editor + dynamic sections).
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initCountdowns();
    initBuyBoxes();
    initSubscribeInfoTooltip();
    initSimpleAtc();
    initCardVariants();
    initMossLabQuiz();
    initStickyAtc();
  });
})();